/**
 * ADR-0016 Phase 5 — Runtime guard: lot quantity must only change via movement projection.
 *
 * Call-sites that apply InventoryMovement deltas wrap work with
 * `runWithInventoryLotProjection`. Direct remaining/total quantity merges outside
 * that scope throw `LOT_QTY_WITHOUT_MOVEMENT` unless `allowLegacyQtyMutation` is set
 * (repair / rebuild only).
 */

let projectionDepth = 0;
let legacyAllowDepth = 0;

export function beginInventoryLotProjection() {
  projectionDepth += 1;
}

export function endInventoryLotProjection() {
  projectionDepth = Math.max(0, projectionDepth - 1);
}

export function isInventoryLotProjectionActive() {
  return projectionDepth > 0;
}

/**
 * @template T
 * @param {() => T} fn
 * @returns {T}
 */
export function runWithInventoryLotProjection(fn) {
  beginInventoryLotProjection();
  try {
    return fn();
  } finally {
    endInventoryLotProjection();
  }
}

/**
 * Escape hatch for intentional rebuild/repair paths that rewrite quantities
 * without replaying the full historical ledger in one transaction.
 * @template T
 * @param {() => T} fn
 * @returns {T}
 */
export function runWithLegacyLotQtyMutation(fn) {
  legacyAllowDepth += 1;
  try {
    return fn();
  } finally {
    legacyAllowDepth = Math.max(0, legacyAllowDepth - 1);
  }
}

export function isLegacyLotQtyMutationAllowed() {
  return legacyAllowDepth > 0;
}

/**
 * @param {{ reason?: string, allowLegacyQtyMutation?: boolean }} [context]
 */
export function assertLotQuantityMutationAllowed(context = {}) {
  if (isInventoryLotProjectionActive()) return;
  if (context.allowLegacyQtyMutation === true || isLegacyLotQtyMutationAllowed()) {
    return;
  }
  const reason = context.reason || 'unknown';
  const err = new Error(
    `ADR-0016 Phase 5: Lot quantity mutation without InventoryMovement projection (${reason})`
  );
  err.code = 'LOT_QTY_WITHOUT_MOVEMENT';
  throw err;
}

/**
 * Detect remaining/total quantity delta between prior and next lot shape.
 * @param {object|null|undefined} before
 * @param {object|null|undefined} after
 * @returns {boolean}
 */
export function lotQuantityChanged(before, after) {
  if (!after) return false;
  if (!before) {
    const rem = Number(after.remainingQuantity);
    const tot = Number(after.totalQuantity);
    return (Number.isFinite(rem) && Math.abs(rem) > 1e-9) ||
      (Number.isFinite(tot) && Math.abs(tot) > 1e-9);
  }
  const fields = ['remainingQuantity', 'totalQuantity'];
  for (const f of fields) {
    const a = Number(before[f]);
    const b = Number(after[f]);
    if (!Number.isFinite(a) && !Number.isFinite(b)) continue;
    if (Math.abs((Number.isFinite(a) ? a : 0) - (Number.isFinite(b) ? b : 0)) > 1e-9) {
      return true;
    }
  }
  return false;
}

function listLotsForGuard(raw) {
  if (Array.isArray(raw)) return raw.filter(Boolean);
  if (raw && typeof raw === 'object') return Object.values(raw).filter(Boolean);
  return [];
}

function productLocationRemainingMap(lots) {
  const map = new Map();
  for (const lot of listLotsForGuard(lots)) {
    if (lot?.productId == null) continue;
    const loc = String(lot.location || 'Principal');
    const key = `${String(lot.productId)}|${loc}`;
    map.set(key, (map.get(key) || 0) + (Number(lot.remainingQuantity) || 0));
  }
  return map;
}

/**
 * True when product@location remaining totals match (structural reshape / consolidate OK).
 */
export function productLocationRemainingMapsMatch(a, b, epsilon = 1e-6) {
  const ma = productLocationRemainingMap(a);
  const mb = productLocationRemainingMap(b);
  if (ma.size !== mb.size) return false;
  for (const [key, rem] of ma) {
    if (!mb.has(key)) return false;
    if (Math.abs(rem - mb.get(key)) > epsilon) return false;
  }
  return true;
}

/**
 * Guard bulk `parsed.lots = next` replacements that change quantities.
 * Allowed without projection when:
 * - No per-id remaining/total change, OR
 * - product@location remaining sums are preserved (dedupe / consolidate reshape)
 * @param {object[]|Record|null|undefined} beforeLots
 * @param {object[]|Record|null|undefined} afterLots
 * @param {{ reason?: string, allowLegacyQtyMutation?: boolean, epsilon?: number }} [context]
 */
export function assertLotsArrayReplaceAllowed(beforeLots, afterLots, context = {}) {
  if (isInventoryLotProjectionActive()) return;
  if (context.allowLegacyQtyMutation === true || isLegacyLotQtyMutationAllowed()) {
    return;
  }

  const beforeList = listLotsForGuard(beforeLots);
  const afterList = listLotsForGuard(afterLots);
  const beforeById = new Map(
    beforeList.filter((l) => l?.id != null).map((l) => [String(l.id), l])
  );
  const afterById = new Map(
    afterList.filter((l) => l?.id != null).map((l) => [String(l.id), l])
  );
  const ids = new Set([...beforeById.keys(), ...afterById.keys()]);
  let anyQtyChange = false;
  for (const id of ids) {
    const b = beforeById.get(id);
    const a = afterById.get(id);
    if (!a) {
      if (b && lotQuantityChanged(null, b)) {
        anyQtyChange = true;
        break;
      }
      continue;
    }
    if (lotQuantityChanged(b || null, a)) {
      anyQtyChange = true;
      break;
    }
  }
  if (!anyQtyChange) return;

  // Structural reshape that preserves sellable stock per product@location
  // (only when product keys exist — bare rem rows without productId stay strict)
  const epsilon = context.epsilon != null ? Number(context.epsilon) : 1e-6;
  const beforeMap = productLocationRemainingMap(beforeList);
  if (
    beforeMap.size > 0 &&
    productLocationRemainingMapsMatch(beforeList, afterList, epsilon)
  ) {
    return;
  }

  assertLotQuantityMutationAllowed({
    ...context,
    reason: context.reason || 'lots_array_replace'
  });
}

/**
 * Assign parsed.lots only when qty-preserving / structural, or under projection / legacy.
 * @param {object} parsed
 * @param {object[]} nextLots
 * @param {{ reason?: string, allowLegacyQtyMutation?: boolean }} [context]
 */
export function assignParsedLotsGuarded(parsed, nextLots, context = {}) {
  if (!parsed || typeof parsed !== 'object') return nextLots;
  assertLotsArrayReplaceAllowed(parsed.lots, nextLots, context);
  parsed.lots = nextLots;
  return nextLots;
}

/**
 * Guard a merge of `lot` into existing lot row (or insert).
 * @param {object|null|undefined} existing
 * @param {object} lot
 * @param {{ reason?: string, allowLegacyQtyMutation?: boolean }} [context]
 */
export function assertLotMergeQuantityAllowed(existing, lot, context = {}) {
  if (!lotQuantityChanged(existing, lot)) return;
  assertLotQuantityMutationAllowed(context);
}
